import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart3, X, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { resolveFileUrl } from '../../utils/resolveFileUrl';

const getInitials = (name, email) => {
  if (name && name.trim()) {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.trim().slice(0, 2).toUpperCase();
  }
  if (email && email.trim()) return email.trim().slice(0, 2).toUpperCase();
  return '?';
};

/** Small avatar for voter: photo or initials fallback */
const VoterAvatar = ({ name, email, avatar_url, isOwnMessage }) => {
  const [resolvedUrl, setResolvedUrl] = useState(null);
  useEffect(() => {
    if (!avatar_url) return;
    if (avatar_url.startsWith('http')) {
      setResolvedUrl(avatar_url);
      return;
    }
    resolveFileUrl(avatar_url).then(setResolvedUrl).catch(() => setResolvedUrl(null));
  }, [avatar_url]);
  const initials = getInitials(name, email);
  const bg = isOwnMessage ? 'bg-white/30' : 'bg-[#2563eb]/30 dark:bg-primary-400/30';
  const textCls = isOwnMessage ? 'text-white' : 'text-gray-700 dark:text-gray-200';
  if (resolvedUrl) {
    return (
      <img
        src={resolvedUrl}
        alt={name || email || ''}
        className="w-6 h-6 rounded-full object-cover border border-white/50 flex-shrink-0"
      />
    );
  }
  return (
    <span
      className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium flex-shrink-0 ${bg} ${textCls}`}
      title={name || email || ''}
    >
      {initials}
    </span>
  );
};

/**
 * Renders poll message as a card. Fetches poll options and vote counts, allows voting.
 * If !is_secret: shows last 3 voters' avatars per option + "Oyları Görüntüle" modal.
 */
const PollCardMessage = ({ message, isOwnMessage, messageId, chatType = 'dm', groupId = null }) => {
  const { t } = useTranslation('chat');
  const { user } = useAuth();
  const { getPoll, votePoll, getPollVotesDetail, lastPollVoted } = useChat();
  const [pollData, setPollData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [votesDetailOpen, setVotesDetailOpen] = useState(false);
  const [votesDetail, setVotesDetail] = useState(null);
  const [votesDetailLoading, setVotesDetailLoading] = useState(false);

  const pollId = message?.poll_id || null;
  const mid = messageId || message?.id;

  const fetchPoll = useCallback(async () => {
    if (!pollId) return;
    setLoading(true);
    try {
      const res = await getPoll(pollId);
      if (res?.poll && res?.options) setPollData(res);
      else setPollData(null);
    } catch (e) {
      console.warn('PollCardMessage fetch error:', e);
      setPollData(null);
    } finally {
      setLoading(false);
    }
  }, [pollId, getPoll]);

  useEffect(() => {
    fetchPoll();
  }, [fetchPoll]);

  useEffect(() => {
    if (!lastPollVoted?.pollId || lastPollVoted.pollId !== pollId) return;
    const forDm = chatType === 'dm';
    if (forDm && lastPollVoted.messageId != null && lastPollVoted.messageId !== mid) return;
    if (!forDm && lastPollVoted.groupId != null && lastPollVoted.groupId !== groupId) return;
    if (!forDm && lastPollVoted.groupMessageId != null && lastPollVoted.groupMessageId !== mid) return;
    fetchPoll();
  }, [lastPollVoted?.pollId, lastPollVoted?.messageId, lastPollVoted?.groupId, lastPollVoted?.groupMessageId, pollId, mid, chatType, groupId, fetchPoll]);

  const handleVote = async (optionId, multipleChoice) => {
    if (!pollId) return;
    if (!multipleChoice) {
      await votePoll(pollId, optionId);
      return;
    }
    const currentVoted = (pollData?.options || []).filter((o) => o.voted).map((o) => o.id);
    const next = currentVoted.includes(optionId)
      ? currentVoted.filter((id) => id !== optionId)
      : [...currentVoted, optionId];
    await votePoll(pollId, next);
  };

  const openVotesDetail = useCallback(async () => {
    if (!pollId) return;
    setVotesDetailOpen(true);
    setVotesDetailLoading(true);
    setVotesDetail(null);
    try {
      const res = await getPollVotesDetail(pollId);
      setVotesDetail(res);
    } catch (e) {
      console.warn('Poll votes detail error:', e);
      setVotesDetail(null);
    } finally {
      setVotesDetailLoading(false);
    }
  }, [pollId, getPollVotesDetail]);

  const cardBg  = isOwnMessage ? 'bg-[#1d3557] border-[#3d8ef0]/25' : 'bg-[var(--color-bg-muted)] border-white/[0.09]';
  const metaColor = 'text-[var(--color-text-muted)]';

  if (!pollId && !message?.poll_title) {
    return (
      <div className={`rounded-2xl border ${cardBg} overflow-hidden min-w-[240px] max-w-[320px]`}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.06]">
          <BarChart3 className="w-4 h-4 flex-shrink-0 text-[#3d8ef0]" />
          <span className="text-[11px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">{t('pollCard.header')}</span>
        </div>
        <div className="px-4 py-3">
          <p className="text-sm text-[var(--color-text-muted)]">{t('common:loading')}</p>
        </div>
      </div>
    );
  }

  const title = pollData?.poll?.title || message?.poll_title || t('pollCard.titleFallback');
  const multipleChoice = pollData?.poll?.multiple_choice === true || message?.poll_multiple_choice === true;
  const isSecret = pollData?.poll?.is_secret === true;
  const options = pollData?.options || [];

  const totalVotes = options.reduce((sum, o) => sum + (o.voteCount || 0), 0);
  const maxVotes = Math.max(...options.map((o) => o.voteCount || 0), 1);

  return (
    <>
    <div className={`rounded-2xl border ${cardBg} overflow-hidden min-w-[240px] max-w-[320px] w-full`}>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.06]">
        <BarChart3 className="w-4 h-4 flex-shrink-0 text-[#3d8ef0]" />
        <span className="text-[11px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">Anket</span>
      </div>
      {/* Body */}
      <div className="px-4 py-3">
        <p className="text-sm font-semibold text-[var(--color-text-primary)] mb-3 break-words">{title}</p>

        {loading && !pollData ? (
          <p className={`text-xs ${metaColor}`}>{t('common:loading')}</p>
        ) : (
          <div className="space-y-2">
            {options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleVote(opt.id, multipleChoice)}
                disabled={loading}
                className={`w-full text-left px-3 py-2 rounded-xl border transition-colors ${
                  opt.voted
                    ? 'bg-[#2563eb]/15 border-[#3d8ef0]/40'
                    : isOwnMessage
                      ? 'border-white/15 hover:bg-white/[0.08]'
                      : 'border-white/[0.08] hover:bg-[#1e2d47]'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-[var(--color-text-primary)] truncate">{opt.text}</span>
                  <span className={`text-[11px] ${metaColor} flex-shrink-0`}>{t('pollCard.voteCount', { count: opt.voteCount ?? 0 })}</span>
                </div>
                {totalVotes > 0 && (
                  <div className="mt-1.5 h-1 rounded-full bg-white/[0.08]">
                    <div
                      className="h-full rounded-full bg-[#2563eb] transition-all"
                      style={{ width: `${((opt.voteCount || 0) / maxVotes) * 100}%` }}
                    />
                  </div>
                )}
                {!isSecret && (opt.lastVoters || []).length > 0 && (
                  <div className="flex items-center gap-0.5 mt-1.5 -space-x-1.5">
                    {(opt.lastVoters || []).slice(0, 3).map((v) => (
                      <VoterAvatar key={v.id} name={v.name} email={null} avatar_url={v.avatar_url} isOwnMessage={isOwnMessage} />
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {multipleChoice && (
          <p className={`text-[11px] ${metaColor} mt-2`}>{t('pollCard.multipleChoiceHint')}</p>
        )}

        {!isSecret && totalVotes > 0 && (
          <button
            type="button"
            onClick={openVotesDetail}
            className={`mt-3 flex items-center gap-1.5 text-[11px] font-medium rounded-lg px-2 py-1.5 transition-colors ${metaColor} hover:text-[var(--color-text-secondary)] hover:bg-white/[0.05]`}
          >
            <Users className="w-3.5 h-3.5" />
            {t('pollCard.viewVotes')}
          </button>
        )}
      </div>
    </div>
    <AnimatePresence>
        {votesDetailOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setVotesDetailOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-md max-h-[80vh] flex flex-col bg-[var(--color-bg-base)] rounded-2xl shadow-2xl border border-white/[0.08] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.07] flex-shrink-0">
                <h3 className="text-base font-semibold text-[var(--color-text-primary)]">{t('pollCard.votesModalTitle')}</h3>
                <button
                  type="button"
                  onClick={() => setVotesDetailOpen(false)}
                  className="p-1.5 rounded-full text-[var(--color-text-muted)] hover:bg-white/[0.05] hover:text-[var(--color-text-secondary)] transition-colors"
                  aria-label={t('common:close')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 overflow-y-auto flex-1 min-h-0">
                {votesDetailLoading ? (
                  <p className="text-sm text-[var(--color-text-muted)]">{t('common:loading')}</p>
                ) : votesDetail?.options ? (
                  <div className="space-y-4">
                    {votesDetail.options.map((opt) => (
                      <div key={opt.id}>
                        <p className="text-sm font-semibold text-[var(--color-text-secondary)] mb-2">{opt.text}</p>
                        <ul className="space-y-1.5">
                          {(opt.voters || []).map((v) => (
                            <li key={v.id} className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
                              <VoterAvatar name={v.name} email={v.email} avatar_url={v.avatar_url} isOwnMessage={false} />
                              <span>{v.name || v.email || t('unknownUser')}</span>
                            </li>
                          ))}
                          {(!opt.voters || opt.voters.length === 0) && (
                            <li className="text-xs text-[var(--color-text-muted)]/60">{t('pollCard.noVotesYet')}</li>
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--color-text-muted)]">{t('pollCard.votesLoadFailed')}</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PollCardMessage;
